import { useCallback, useEffect, useRef, useState } from 'react';
import { useCinematicStore } from '../../store/cinematicSlice';
import { cinematicService } from '../../services/CinematicService';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { NineSliceFrame } from './NineSliceFrame';
import { Icon } from './Icon';
import { KeyDisplay } from './KeyDisplay';
import './DialogueScreen.scss';

const TYPE_INTERVAL_MS = 28;

export const DialogueScreen = () => {
  const dialogue = useCinematicStore((s) => s.dialogue);
  const [shown, setShown] = useState(0);
  const fullText = dialogue?.text ?? '';
  const isComplete = shown >= fullText.length;

  useEffect(() => {
    setShown(0);
  }, [dialogue]);

  useEffect(() => {
    if (!dialogue || isComplete) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setShown((n) => Math.min(fullText.length, n + 1));
    }, TYPE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [dialogue, isComplete, fullText.length]);

  const advance = useCallback(() => {
    if (!dialogue) {
      return;
    }
    if (!isComplete) {
      setShown(fullText.length);
      return;
    }
    cinematicService.resolveDialogue();
  }, [dialogue, isComplete, fullText.length]);

  const isAuto = dialogue?.durationMs !== undefined;

  useEffect(() => {
    if (!dialogue || !isAuto || !isComplete) {
      return undefined;
    }
    const id = window.setTimeout(
      () => cinematicService.resolveDialogue(),
      dialogue.durationMs,
    );
    return () => window.clearTimeout(id);
  }, [dialogue, isAuto, isComplete]);

  const advanceRef = useRef(advance);
  advanceRef.current = advance;

  const { containerRef } = useMenuNavigation({
    onConfirm: isAuto ? undefined : () => advanceRef.current(),
    onCancel: isAuto ? undefined : () => advanceRef.current(),
  });

  if (!dialogue) {
    return null;
  }

  return (
    <div className="dialogue-overlay">
      <NineSliceFrame className="dialogue-frame">
        <div className="dialogue-body" ref={containerRef}>
          {dialogue.speaker && (
            <span className="dialogue-name">{dialogue.speaker}</span>
          )}
          {dialogue.portraitId && (
            <div className="dialogue-portrait">
              <Icon sprite={dialogue.portraitId} size={56} />
            </div>
          )}
          <p
            className="dialogue-text"
            data-nav-row={0}
            data-nav-col={0}
            data-nav-id="advance"
          >
            {fullText.slice(0, shown)}
          </p>
          {!isAuto && isComplete && (
            <div className="dialogue-hint">
              <KeyDisplay context="interface" action="CONFIRM" size="sm" />{' '}
              continuer
            </div>
          )}
        </div>
      </NineSliceFrame>
    </div>
  );
};
